import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../i18n'
import { LANGUAGES } from '../i18n'

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0f1419',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: 32,
    objectFit: 'contain',
  },
  title: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    marginBottom: 40,
    textAlign: 'center',
  },
  cardsWrap: {
    width: '100%',
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  card: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '20px 20px 20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    minHeight: 90,
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardContent: {
    flex: 1,
  },
  cardTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 4,
  },
  cardDesc: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    lineHeight: 1.4,
  },
  arrow: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 20,
    flexShrink: 0,
  },
}

const QrScanIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="3" height="3" />
    <line x1="21" y1="14" x2="21" y2="14.01" />
    <line x1="21" y1="21" x2="21" y2="21.01" />
    <line x1="14" y1="21" x2="17" y2="21" />
  </svg>
)

const BriefcaseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    <line x1="2" y1="13" x2="22" y2="13" />
  </svg>
)

export default function RoleSelector() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [pressed, setPressed] = useState<string | null>(null)

  const currentLang = LANGUAGES.find(l => l.code === i18n.language)

  const handlePress = (role: string, path: string) => {
    setPressed(role)
    setTimeout(() => navigate(path), 150)
  }

  return (
    <div style={styles.page}>
      <img src="/logo.png" alt="RailSahayak" style={styles.logo} />
      <div style={styles.title}>{t('role_select_title', 'Welcome to RailSahayak')}</div>
      <div style={styles.subtitle}>{t('role_select_subtitle', 'Select your role to continue')}</div>

      <div style={styles.cardsWrap}>
        <div
          style={{
            ...styles.card,
            borderLeft: '4px solid #ff6b35',
            transform: pressed === 'staff' ? 'scale(0.97)' : 'scale(1)',
          }}
          onClick={() => handlePress('staff', '/staff/login')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePress('staff', '/staff/login')}
        >
          <div style={{ ...styles.cardIcon, background: 'rgba(255,107,53,0.15)' }}>
            <QrScanIcon />
          </div>
          <div style={styles.cardContent}>
            <div style={styles.cardTitle}>{t('role_staff', 'I am Staff')}</div>
            <div style={styles.cardDesc}>
              {t('role_staff_desc', 'Scan QR codes and upload work evidence')}
            </div>
          </div>
          <div style={styles.arrow}>›</div>
        </div>

        <div
          style={{
            ...styles.card,
            borderLeft: '4px solid #3b82f6',
            transform: pressed === 'admin' ? 'scale(0.97)' : 'scale(1)',
          }}
          onClick={() => handlePress('admin', '/login')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handlePress('admin', '/login')}
        >
          <div style={{ ...styles.cardIcon, background: 'rgba(59,130,246,0.15)' }}>
            <BriefcaseIcon />
          </div>
          <div style={styles.cardContent}>
            <div style={styles.cardTitle}>{t('role_admin', 'Contractor / Admin')}</div>
            <div style={styles.cardDesc}>
              {t('role_admin_desc', 'Manage tenders, evaluations and reports')}
            </div>
          </div>
          <div style={styles.arrow}>›</div>
        </div>
      </div>

      <button
        style={{
          marginTop: 32,
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.35)',
          fontSize: 13,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          borderRadius: 8,
        }}
        onClick={() => navigate('/language')}
      >
        <span style={{ fontSize: 15 }}>🌐</span>
        {currentLang ? currentLang.name : t('lang_select', 'Language')}
      </button>
    </div>
  )
}
